<template>
  <!-- The site's tab switcher: a Listbox on mobile, a row of tabs from `sm`.
       Each tab is `{ name, subName, current, count? }`; `count` renders as the
       "Colors: n" badge and is omitted for tabs that don't count anything.
       Emits `change(tab)` with the picked tab.

       Layout-free on purpose — PaletteTabs wraps it in the generator's content
       column, the brand pages drop it straight into their own container. -->
  <div class="bg-gray-950">
    <div class="sm:hidden">
      <Listbox as="div" :model-value="currentTab" @update:model-value="(tab) => $emit('change', tab)">
        <ListboxLabel class="sr-only">Select a tab</ListboxLabel>
        <div class="relative mt-2">
          <ListboxButton class="relative w-full cursor-pointer rounded-md bg-gray-900 py-1.5 pl-3 pr-10 text-left text-white shadow-sm ring-1 ring-inset ring-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
            <span class="block truncate">{{ currentTab?.name }}</span>
            <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <ChevronUpDownIcon class="h-5 w-5 text-gray-400" aria-hidden="true" />
            </span>
          </ListboxButton>

          <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
            <ListboxOptions class="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-gray-800 bg-gray-900 py-1 text-base shadow-xl shadow-black/40 focus:outline-none">
              <ListboxOption v-for="tab in tabs" :key="tab.name" v-slot="{ active }" as="template" :value="tab">
                <li class="relative cursor-pointer select-none py-2 pl-3 pr-9" :class="tab.current ? 'bg-indigo-600 text-white' : active ? 'bg-gray-800 text-white' : 'text-gray-300'">
                  <div class="flex items-center gap-1">
                    <span class="block truncate" :class="tab.current ? 'font-semibold' : 'font-normal'">{{ tab.name }}</span>
                    <BaseBadge v-if="tab.count !== undefined && !isLoading" :tone="tab.current ? 'brand' : 'neutral'">
                      Colors: {{ tab.count }}
                    </BaseBadge>
                  </div>
                  <span class="mt-1 block truncate text-xs" :class="tab.current ? 'font-semibold' : 'font-normal'">{{ tab.subName }}</span>

                  <span v-if="tab.current" class="absolute inset-y-0 right-0 flex items-center pr-4 text-white">
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <nav class="hidden pt-4 sm:block" aria-label="Palette views">
      <ul role="list" class="flex min-w-full gap-x-6 border-b border-gray-800 text-sm font-semibold leading-6 text-gray-400">
        <!-- The tab name stays a heading (they're the page's section titles),
             so the button sits inside it and stretches over the whole tab. -->
        <li
          v-for="tab in tabs"
          :key="tab.name"
          class="relative pb-3 transition hover:text-gray-300 has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:-outline-offset-2 has-[:focus-visible]:outline-indigo-500"
          :class="[tab.current ? '-mb-px border-b-2 border-indigo-400' : '', widthClass]"
        >
          <component :is="headingTag" class="w-full text-xl font-bold" :class="tab.current ? 'text-indigo-400' : ''">
            <button
              type="button"
              class="text-left after:absolute after:inset-0 focus-visible:outline-none"
              :aria-pressed="tab.current"
              @click="$emit('change', tab)"
            >
              {{ tab.name }}
            </button>
          </component>
          <p class="w-full text-xs" :class="tab.current ? 'text-gray-400' : ''">{{ tab.subName }}</p>
          <BaseBadge v-if="isLoading" class="mt-3">
            Colors:
            <SpinnerIcon class="h-3 w-3 text-white" />
          </BaseBadge>
          <BaseBadge v-else-if="tab.count !== undefined" class="mt-3">Colors: {{ tab.count }}</BaseBadge>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Listbox, ListboxButton, ListboxLabel, ListboxOptions, ListboxOption } from '@headlessui/vue'
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/20/solid'
import BaseBadge from '~/components/atoms/BaseBadge.vue'
import SpinnerIcon from '~/components/atoms/SpinnerIcon.vue'

const props = defineProps({
  // [{ name, subName, current, count? }]
  tabs: { type: Array, required: true },
  isLoading: { type: Boolean, default: false },
  // The generator's tabs are the page's section headings; on a brand page the
  // real headings sit above, so the labels drop to plain text.
  headingTag: { type: String, default: 'h2' },
})
defineEmits(['change'])

const currentTab = computed(() => props.tabs.find((tab) => tab.current) ?? props.tabs[0])
// Both literals are spelled out so Tailwind emits them — a computed class name
// never reaches the JIT scanner.
const widthClass = computed(() => (props.tabs.length === 2 ? 'w-1/2' : 'flex-1'))
</script>
